import React, {useCallback, useEffect, useMemo} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {useTranslation} from 'react-i18next';
import {formatDuration, useStyles} from './AccountControlPanel.utils';
import CustomizedIconButton from '../../components/IconButton/IconButton';
import {Stop, AddBox} from '@material-ui/icons';
import { actions } from '../../store';
import { ReduxState } from '../../store/types';
import ForwardingModeDropdown from './ForwardingModeDropdown/ForwardingModeDropdown';
import dayjs from 'dayjs';
import { DefaultAnsweringActionFlag, ServiceFeature, ServiceFeatureName } from '../../store/types/ServiceFeature';
import ExtensionDIDNumber from '../../components/Extensions/ExtensionDIDNumber';
import { Colors } from '../../styles/Colors';
import BarLoader from '../../components/BarLoader/BarLoader';
import classNames from 'classnames';
import { showToast } from '../../utils/showToast';
import { AccountPermission } from '../../store/types/Permission';
import PermissionProvider from '../../components/PermissionProvider/PermissionProvider';

const AccountControlPanel = () => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {isLoadingAccountDetails, accountDetails,
        isLoadingActiveSessionsInfo, activeSessionInfo
    } = useSelector((state: ReduxState) => state.accountControlPanel);

    useEffect(() => {
        dispatch(actions.getAccountPanelAccountDetails.request());
    }, []);

    useEffect(() => {
        if(accountDetails?.i_account) {
            dispatch(actions.getLastWrapUpActiveCall.request({
                i_account: accountDetails.i_account
            }));
        }
    }, [accountDetails]);

    const numbers = useMemo(() => {
        if(accountDetails?.alias_did_number_list?.length)
            return  accountDetails?.alias_did_number_list?.map(e => e.did_number) ?? [];
        if(accountDetails?.did_number?.length)
            return [accountDetails.did_number];
        return [];
    }, [accountDetails]);
    
    const wrapUpTimeLabel = useMemo(() => {
        if(!accountDetails || !activeSessionInfo?.release_time) return '';

        const utc = dayjs.utc(activeSessionInfo?.release_time);
        const utcNow = dayjs.utc();
        const diffSeconds = utc.diff(utcNow, 'second');
        if(diffSeconds < 0) return '00:00';

        return formatDuration(diffSeconds);
    }, [accountDetails, activeSessionInfo]);

    const onStopTimeClick = useCallback(() => {
        if(accountDetails) {
            dispatch(actions.setWrapUpTime.request({
                i_account: accountDetails.i_account,
                wrap_up_time: 0,
                callback: () => {
                    dispatch(actions.getLastWrapUpActiveCall.request({
                        i_account: accountDetails.i_account
                    }));
                }
            }));
        }
    }, [accountDetails]);

    const onAddTimeClick = useCallback(() => {
        if(accountDetails && activeSessionInfo) {
            dispatch(actions.setWrapUpTime.request({
                i_account: accountDetails.i_account,
                wrap_up_time: activeSessionInfo.wrap_up_extend_time ?? 1,
                callback: () => {
                    dispatch(actions.getLastWrapUpActiveCall.request({
                        i_account: accountDetails.i_account
                    }));
                }
            }));
        }
    }, [accountDetails, activeSessionInfo]);
    
    const onChangeForwardingModel = useCallback((value: DefaultAnsweringActionFlag) => {
        if(accountDetails) {
            const serviceFeatures: ServiceFeature[] = [];
            const existingServiceFeature = accountDetails?.service_features
                ?.find(e => e.name == ServiceFeatureName.DefaultAction);
            if(existingServiceFeature) {
                serviceFeatures.push({
                    ...existingServiceFeature,
                    effective_flag_value: value + '',
                    flag_value: value + '',
                });
            } else {
                serviceFeatures.push({
                    name: ServiceFeatureName.DefaultAction,
                    effective_flag_value: value + '',
                    flag_value: value + '',
                    attributes: [{
                        name: "timeout",
                        effective_values: ["30"],
                        values: ["30"]
                    }]
                });
            }
            dispatch(actions.updateForwardingModel.request({
                i_account: accountDetails.i_account,
                serviceFeatures: serviceFeatures,
                callback: () => {
                    showToast(t('screens:accountDashboard.settingsHaveBeenUpdated'));
                    dispatch(actions.getAccountPanelAccountDetails.request());
                }
            }));
        }
    }, [accountDetails]);

    useEffect(() => {
        const intervalId = setInterval(() => {
            if(activeSessionInfo) {
                const diffSeconds = dayjs.utc(activeSessionInfo.release_time).diff(dayjs.utc(), 'second');
                if(diffSeconds < 0) {
                    dispatch(actions.getLastWrapUpActiveCall.success(undefined));
                } else {
                    dispatch(actions.getLastWrapUpActiveCall.success({
                        ...activeSessionInfo
                    }));
                }
            }
        }, 250);

        return () => {
            clearInterval(intervalId);
        };
    }, [activeSessionInfo]);
    
    const formatSeconds = function(seconds: number): string {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;

        const parts: string[] = [];

        if (mins > 0) {
            parts.push(`${mins} ${t('units:time.minutes')}`);
        }

        if (secs > 0 || mins === 0) {
            parts.push(`${secs} ${t('units:time.seconds')}`);
        }

        return parts.join(' ');
    }

    if(isLoadingAccountDetails || isLoadingActiveSessionsInfo) {
        return (<div className={classes.accountControlPanelContainer}>
            <div className={classes.barLoaderContainer}>
                <BarLoader containerClass={classes.barLoader} backgroundColor={Colors.LightGraySecondary4} />
                <BarLoader containerClass={classNames(classes.barLoader, classes.barLoader2)} backgroundColor={Colors.LightGraySecondary4} />
            </div>
        </div>);
    }

    return (<div className={classes.accountControlPanelContainer}>
        <PermissionProvider permission={AccountPermission.Dashboard.WrapUp.value}>
            {activeSessionInfo && (
                <div className={classes.wrapUpTimeContainer}>
                    <div className={classes.wrapUpLabel}>{t('screens:accountDashboard.wrapUpTime')}</div>
                    <div className={classes.timeLabel}>{wrapUpTimeLabel}</div>
                    <div className={classes.stopTimerButtonContainer}>
                        <CustomizedIconButton
                            tooltipText={t(
                                'tooltips:accountDashboard.stopTimer',
                            )}
                            dataQa="stop-timer-icon"
                            dataTestId="stop-timer-icon"
                            onClick={onStopTimeClick}
                            className={classes.stopTimerButton}
                        >
                            <Stop/>
                        </CustomizedIconButton>
                    </div>
                    <div className={classes.addTimeButtonContainer}>
                        <CustomizedIconButton
                            tooltipText={t(
                                'tooltips:accountDashboard.addTime',
                                {
                                    value: formatSeconds(activeSessionInfo?.wrap_up_extend_time ?? 0)
                                }
                            )}
                            dataQa="add-time-icon"
                            dataTestId="add-time-icon"
                            onClick={onAddTimeClick}
                            className={classes.addTimeButton}
                        >
                            <AddBox/>
                        </CustomizedIconButton>
                    </div>
                </div>
            )}
        </PermissionProvider>
        {numbers.length ? (
            <div className={classes.didNumberContainer}>
                <div className={classes.didNumberText}>
                    {t('screens:accountDashboard.didNumber')}
                </div>
                <div className={classes.didNumberDetailsContainer}>
                    <ExtensionDIDNumber
                        showFooter={false}
                        extNumbers={numbers}
                        numberClassName={classes.didNumberValueLabel}
                    />
                </div>
            </div>
        ) : (<></>)}
        {accountDetails && (
            <div className={classes.statusContainer}>
                <ForwardingModeDropdown account={accountDetails} onChange={onChangeForwardingModel} />
            </div>
        )}
    </div>);
};

export default AccountControlPanel;
